import { useMemo, useState } from 'react';
import type {
  FormConsentSnapshot,
  FormFieldDefinition,
  ResolvedFormBrand,
} from '@engage/types';
import { isVisible } from './conditional';
import { brandToCssVars } from './brand';
import './FormRenderer.css';

/**
 * Pure data-in-data-out form renderer. No TanStack, no router, no Zustand,
 * no app-specific imports. The CSS is shipped alongside.
 *
 * Task 4 lifts this folder verbatim into the embed bundle — so any
 * framework leakage here becomes a refactor later. Keep it portable.
 */

export type RendererMode = 'preview' | 'live';

export interface FormRendererProps {
  fields: FormFieldDefinition[];
  brand: ResolvedFormBrand;
  consent: FormConsentSnapshot | null;
  mode: RendererMode;
  values?: Record<string, unknown>;
  errors?: Record<string, string>;
  onChange?: (values: Record<string, unknown>) => void;
  onSubmit?: (values: Record<string, unknown>, consent: { granted: boolean; text: string } | null) => void;
  onFieldFocus?: (fieldKey: string) => void;
  submitLabel?: string;
  /// Renders all fields ignoring conditional logic, with a small badge on
  /// conditionally-shown fields. Only used in preview mode.
  showAllConditional?: boolean;
  /// Currently selected field key — shown with an outline. Preview mode only.
  /// The builder passes this so clicking a field in the preview switches the
  /// right-pane field-settings panel to that field.
  selectedFieldKey?: string | null;
}

export function FormRenderer(props: FormRendererProps) {
  const [internalValues, setInternalValues] = useState<Record<string, unknown>>(props.values ?? {});
  const values = props.onChange ? (props.values ?? internalValues) : internalValues;
  const [consentChecked, setConsentChecked] = useState(false);

  const style = useMemo(() => brandToCssVars(props.brand), [props.brand]);

  const setValue = (key: string, value: unknown) => {
    const next = { ...values, [key]: value };
    if (props.onChange) props.onChange(next);
    else setInternalValues(next);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (props.mode === 'preview') return;
    const consent = props.consent ? { granted: consentChecked, text: props.consent.text } : null;
    props.onSubmit?.(values, consent);
  };

  const showAll = props.mode === 'preview' && (props.showAllConditional ?? true);

  return (
    <form
      className="jt-form-renderer"
      style={style as React.CSSProperties}
      onSubmit={handleSubmit}
      noValidate
    >
      {props.brand.logo_url ? (
        <img className="jt-form-logo" src={props.brand.logo_url} alt="" />
      ) : null}
      <div className="jt-form-fields">
        {props.fields.map((field) => {
          if (!showAll && !isVisible(field, values)) return null;
          const isConditional = !!field.conditional_logic;
          const isSelected = props.selectedFieldKey === field.key;
          return (
            <FieldWrapper
              key={field.key}
              field={field}
              error={props.errors?.[field.key]}
              showConditionalBadge={showAll && isConditional}
              isPreview={props.mode === 'preview'}
              isSelected={isSelected}
              onClick={
                props.mode === 'preview' && props.onFieldFocus
                  ? () => props.onFieldFocus!(field.key)
                  : undefined
              }
            >
              {renderControl(field, {
                value: values[field.key],
                onChange: (v) => setValue(field.key, v),
                onFocus: () => props.onFieldFocus?.(field.key),
                disabled: props.mode === 'preview',
              })}
            </FieldWrapper>
          );
        })}
      </div>
      {props.consent ? (
        <ConsentBlock
          snapshot={props.consent}
          checked={consentChecked}
          onChange={setConsentChecked}
        />
      ) : null}
      <button className="jt-form-submit" type="submit" disabled={props.mode === 'preview'}>
        {props.submitLabel ?? 'Submit'}
      </button>
    </form>
  );
}

// ─── pieces ────────────────────────────────────────────────────────────────────

function FieldWrapper(props: {
  field: FormFieldDefinition;
  error?: string;
  showConditionalBadge: boolean;
  children: React.ReactNode;
  isPreview?: boolean;
  isSelected?: boolean;
  onClick?: () => void;
}) {
  // In preview mode the controls are disabled (so the recruiter can't type
  // into them), which means focus events don't fire. We make the whole
  // field row clickable here so clicking anywhere in a previously-added
  // field switches the right-pane settings to it.
  const wrapperClass = [
    'jt-form-field',
    props.isPreview ? 'jt-form-field--preview' : '',
    props.isSelected ? 'jt-form-field--selected' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const handleClick = props.onClick;
  // In live mode, stop mousedown on the inner control wrapper so the
  // wrapper's click handler (if any) doesn't fire twice when the user
  // interacts with the input. In preview mode the input is disabled so
  // double-fires can't happen — and we want every click inside the
  // field row, label included, to bubble up to the wrapper so it can
  // select the field.
  const stopInLive = props.isPreview
    ? undefined
    : (e: React.MouseEvent) => e.stopPropagation();

  if (props.field.type === 'CONSENT_CHECKBOX') {
    return (
      <div
        className={wrapperClass}
        data-key={props.field.key}
        onClick={handleClick}
        role={handleClick ? 'button' : undefined}
        tabIndex={handleClick ? 0 : undefined}
      >
        <div onMouseDown={stopInLive}>{props.children}</div>
        {props.error ? <div className="jt-form-error">{props.error}</div> : null}
      </div>
    );
  }
  return (
    <div
      className={wrapperClass}
      data-key={props.field.key}
      onClick={handleClick}
      role={handleClick ? 'button' : undefined}
      tabIndex={handleClick ? 0 : undefined}
    >
      <label htmlFor={`f_${props.field.key}`}>
        {props.field.label}
        {props.field.required ? <span aria-hidden="true"> *</span> : null}
        {props.showConditionalBadge ? (
          <span className="jt-form-badge" title="Shown only when its condition is met">conditional</span>
        ) : null}
      </label>
      <div onMouseDown={stopInLive}>{props.children}</div>
      {props.field.help_text ? <div className="jt-form-help">{props.field.help_text}</div> : null}
      {props.error ? <div className="jt-form-error">{props.error}</div> : null}
    </div>
  );
}

interface ControlProps {
  value: unknown;
  onChange: (v: unknown) => void;
  onFocus: () => void;
  disabled: boolean;
}

function renderControl(field: FormFieldDefinition, p: ControlProps) {
  const id = `f_${field.key}`;
  const common = { id, onFocus: p.onFocus, disabled: p.disabled };

  switch (field.type) {
    case 'LONG_TEXT':
      return (
        <textarea
          {...common}
          name={field.key}
          value={asString(p.value)}
          onChange={(e) => p.onChange(e.target.value)}
        />
      );
    case 'EMAIL':
      return (
        <input
          {...common}
          name={field.key}
          type="email"
          value={asString(p.value)}
          onChange={(e) => p.onChange(e.target.value)}
        />
      );
    case 'PHONE':
      return (
        <input
          {...common}
          name={field.key}
          type="tel"
          value={asString(p.value)}
          onChange={(e) => p.onChange(e.target.value)}
        />
      );
    case 'NUMBER':
      return (
        <input
          {...common}
          name={field.key}
          type="number"
          step={field.step}
          value={p.value == null ? '' : String(p.value)}
          onChange={(e) => p.onChange(e.target.value === '' ? null : Number(e.target.value))}
        />
      );
    case 'DATE':
      return (
        <input
          {...common}
          name={field.key}
          type="date"
          value={asString(p.value)}
          onChange={(e) => p.onChange(e.target.value)}
        />
      );
    case 'SINGLE_SELECT':
      return (
        <select
          {...common}
          name={field.key}
          value={asString(p.value)}
          onChange={(e) => p.onChange(e.target.value || null)}
        >
          <option value="" />
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case 'MULTI_SELECT':
      return (
        <select
          {...common}
          name={field.key}
          multiple
          value={Array.isArray(p.value) ? (p.value as string[]) : []}
          onChange={(e) =>
            p.onChange(Array.from(e.target.selectedOptions, (o) => o.value))
          }
        >
          {field.options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case 'FILE_UPLOAD':
      // Keeps the File itself: the live host uploads it before submitting and sends the
      // upload token as the answer (JT1-1923).
      return (
        <input
          {...common}
          name={field.key}
          type="file"
          accept={acceptFor(field.accept_preset)}
          onChange={(e) => p.onChange(e.target.files?.[0] ?? null)}
        />
      );
    case 'CONSENT_CHECKBOX':
      return (
        <label className="jt-form-consent-inline">
          <input
            {...common}
            type="checkbox"
            checked={Boolean(p.value)}
            onChange={(e) => p.onChange(e.target.checked)}
          />
          <span>{field.text_override ?? field.label}</span>
        </label>
      );
    case 'ADDRESS':
    case 'SHORT_TEXT':
    default:
      return (
        <input
          {...common}
          name={field.key}
          type="text"
          value={asString(p.value)}
          onChange={(e) => p.onChange(e.target.value)}
        />
      );
  }
}

function ConsentBlock(props: {
  snapshot: FormConsentSnapshot;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  if (props.snapshot.mode === 'NOTICE') {
    return (
      <div className="jt-form-consent">
        <small>{props.snapshot.text}</small>
      </div>
    );
  }
  return (
    <div className="jt-form-consent">
      <label>
        <input
          type="checkbox"
          checked={props.checked}
          onChange={(e) => props.onChange(e.target.checked)}
          required
        />
        <small>{props.snapshot.text}</small>
      </label>
    </div>
  );
}

function asString(v: unknown): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return '';
}

/** The file picker's filter for a field's preset. The server checks the actual bytes. */
export function acceptFor(preset: 'RESUME' | 'ID' | 'IMAGE' | 'ANY_DOC' | undefined): string {
  switch (preset) {
    case 'RESUME':
      return '.pdf,.doc,.docx,.txt';
    case 'ID':
      return '.pdf,.png,.jpg,.jpeg';
    case 'IMAGE':
      return '.png,.jpg,.jpeg';
    default:
      return '.pdf,.doc,.docx,.txt,.png,.jpg,.jpeg';
  }
}
